import React from 'react';
import { Box, Typography, Grid, Button } from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  UserPlus as GroupAddIcon,
  Cloud as CloudQueueIcon,
  BellRing as NotificationsActiveIcon,
  FileText as AssignmentIcon,
  Database as StorageIcon,
  GitBranch as CallSplitIcon
} from 'lucide-react';
import ContentBox from '@root/components/v2/content/ContentBox';
import robotShield from '@root/assets/img/robot.png';
import Loader from '@root/components/common/Loader';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import agent from '@root/agent';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CapitalizeFirstLetter } from '@root/components/utilities/CapitalizeFirstLetter';

const THEME_COLOR = '#1e293b';

const MODULE_DISPLAY_ORDER = [
  { key: 'manual_scan', label: 'Manual Scan' },
  { key: 'real_time_enable', label: 'Real Time Scan' },
  { key: 'one_click_remediation', label: 'One-click Remediation' },
  { key: 'aws_guard_duty', label: 'AWS Guard Duty' },
  { key: 'workload_enable', label: 'Workload Protection' },
  { key: 'threat_anomaly_detection', label: 'Threat/Anomaly Detection' },
  { key: 'kubernetes_security', label: 'Kubernetes Security' },
  { key: 'identity_monitoring', label: 'Identity Protection' },
  { key: 'secret_scanning', label: 'Secret Scanning' },
  { key: 'auto_remediation', label: 'Auto Remediation' },
  { key: 'data_security_enable', label: 'Data Security' },
  { key: 'ecs_security', label: 'ECS Security' },
];

const ProgressTracker = () => {
  const history = useHistory();

  const userInfo = useSelector((state) => {
    return state.user.info.user;
  });
  // Map API module names to step IDs
  const apiToStepId = {
    connect_cloud_account: 'kubernetes',
    ticket_system: 'ticketing',
    alerts: 'alerts',
    invite_teammates: 'teammates',
    code_security: 'repository',
    container_registries: 'registries',
  };
  const { data: configData, isFetching: isFetchingProjectDetails } = useEnhancedQuery(
    ['get_config'],
    () => agent.Tenant_Config.get_config(),
  );
  // Build a lookup for completion and details from configData
  const moduleStatus = {};
  if (configData && configData.data && Array.isArray(configData.data.modules)) {
    configData.data.modules.forEach(mod => {
      if (apiToStepId[mod.name]) {
        moduleStatus[apiToStepId[mod.name]] = {
          completed: mod.completed,
          details: mod.details || null,
        };
      }
    });
  }
 
console.log(configData,'configData')
  const steps = [
    {
      id: 'kubernetes',
      title: 'Connect cloud environment',
      description: 'Enable  security features for your cloud environmnet ',
      icon: <CloudQueueIcon size={24} color={THEME_COLOR} />,
      buttonText: 'Connect another',
      buttonVariant: 'outline',
      details: moduleStatus['kubernetes']?.details || null,
      redirectUrl: '/environments',
    },
    {
      id: 'ticketing',
      title: 'Connect your ticketing system',
      description: 'Create tickets to ensure security issues are followed up on.',
      icon: <AssignmentIcon size={24} color={THEME_COLOR} />,
      buttonText: 'Connect',
      buttonVariant: 'contained',
      redirectUrl: '/integrations',
    },
    {
      id: 'alerts',
      title: 'Set up alert channels',
      description: 'Receive updates through your chosen communication channels when an alert is activated.',
      icon: <NotificationsActiveIcon size={24} color={THEME_COLOR} />,
      buttonText: 'Set up',
      buttonVariant: 'contained',
      redirectUrl: '/integrations',
    },
    {
      id: 'teammates',
      title: 'Invite teammates',
      description: 'Communicate and collaborate with your team during application security findings.',
      icon: <GroupAddIcon size={24} color={THEME_COLOR} />,
      buttonText: 'Invite more',
      buttonVariant: 'outline',
      redirectUrl: '/rbac'
    },
    {
      id: 'registries',
      title: 'Integrate container registries',
      description:
        'Detects known or emerging vulnerabilities from the most popular providers, like: GAR, ECR.',
      icon: <StorageIcon size={24} color={THEME_COLOR} />,
      buttonText: 'Integrate',
      buttonVariant: 'contained',
      redirectUrl: '/integrations',
    },
    {
      id: 'repository',
      title: 'Set up repository',
      description:
        'Address security misconfigurations in lac hosted on a version control system (VCS) or in a local repository.',
      icon: <CallSplitIcon size={24} color={THEME_COLOR} />,
      buttonText: 'Set up',
      buttonVariant: 'contained',
      redirectUrl: '/integrations',
    },
  ];

  // Use API completion status
  const completedSteps = steps.filter(step => moduleStatus[step.id]?.completed);
  const completedCount = completedSteps.length;
  const totalSteps = steps.length;
  const progressPercentage = (completedCount / totalSteps) * 100;

  if (isFetchingProjectDetails) {
    return (
      <Box p={3}>
        <Loader/>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, fontFamily: 'Manrope' }}>
      <Box display="flex" alignItems="center" mb={3} gap={2}>
        <Box
          component="img"
          src={robotShield}
          alt="Robot with Shield"
          sx={{
            borderRadius: '50%',
            bgcolor: '#fff',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
        <Box sx={{ width: 'auto', minWidth: 320, maxWidth: 600 }}>
          <Box
            sx={{
              background: 'linear-gradient(90deg, #2d3a4d 60%, #334155 100%)',
              color: '#fff',
              borderRadius: '18px',
              px: 4,
              py: 2,
              mb: 1,
              fontWeight: 700,
              fontSize: 22,
              boxShadow: '0 2px 8px rgba(30,41,59,0.08)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              width: 'fit-content',
              minWidth: 320,
              maxWidth: 600,
              fontFamily: 'Manrope',
            }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', mb: 0.5, fontFamily: 'Manrope' }}>
              Hi, {CapitalizeFirstLetter(userInfo?.name)}
              <span role="img" aria-label="wave">
                👋
              </span>
            </Typography>
            <Typography variant="body1" sx={{ color: '#e0e7ef', fontFamily: 'Manrope' }}>
              I'm here to help you get set up with CyberQ Shield. Let's go!
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" mt={1}>
            <Box flex={1} mr={2}>
              <Box
                sx={{
                  width: '100%',
                  height: 10,
                  background: '#e6ecf3',
                  borderRadius: 6,
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                <Box
                  sx={{
                    width: `${progressPercentage}%`,
                    height: '100%',
                    background: '#22c55e',
                    borderRadius: 6,
                    transition: 'width 0.5s',
                  }}
                />
              </Box>
            </Box>
            <Typography variant="body1" fontWeight={700} sx={{ color: '#1e293b', minWidth: 120, textAlign: 'right', fontFamily: 'Manrope' }}>
              {completedCount} of {totalSteps} completed
            </Typography>
          </Box>
        </Box>
      </Box>
      <Grid container spacing={3}>
        {steps.map((step, idx) => (
          <Grid item xs={12} sm={6} md={4} key={step.id}>
            <ContentBox
              minHeight="150px"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 2px 16px 0 rgba(30,41,59,0.08)',
                borderRadius: 12,
                border: `1px solid #e5e7eb`,
                padding: 20,
                transition: 'box-shadow 0.2s',
                position: 'relative',
                height: '100%',
                fontFamily: 'Manrope',
              }}>
              <Box display="flex" alignItems="center" mb={1}>
                {step.icon}
                <Typography variant="subtitle1" fontWeight={700} ml={1} color={THEME_COLOR} sx={{ flex: 1, fontFamily: 'Manrope' }}>
                  {step.title}
                </Typography>
                {moduleStatus[step.id]?.completed && <CheckCircleIcon size={20} color="#22c55e" />}
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2} fontWeight={500} sx={{ fontFamily: 'Manrope' }}>
                {step.description}
              </Typography>
              {/* Show module details for the first card if available */}
              {idx === 0 && step.details && (
                <Box mb={2}>
                  {/* <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Module Details:
                  </Typography> */}
                  <Box sx={{ pl: 0, mb: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Manrope' }}>
                      <b>Enabled:</b>{' '}
                      {MODULE_DISPLAY_ORDER.filter(({ key }) => step.details[key])
                        .map(({ label }) => label)
                        .join(', ') || 'None'}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Manrope' }}>
                      <b>Disabled:</b>{' '}
                      {MODULE_DISPLAY_ORDER.filter(({ key }) => step.details[key] === false)
                        .map(({ label }) => label)
                        .join(', ') || 'None'}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box>
                <Button
                  variant={step.buttonVariant === 'contained' ? 'contained' : 'outlined'}
                  size="small"
                  disabled={!!moduleStatus[step.id]?.completed}
                  sx={
                    step.buttonVariant === 'contained'
                      ? {
                          background: THEME_COLOR,
                          color: '#fff',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderColor: THEME_COLOR,
                          boxShadow: 'none',
                          fontFamily: 'Manrope',
                          '&:hover': {
                            background: '#334155',
                            color: '#fff',
                            borderColor: THEME_COLOR,
                          },
                        }
                      : {
                          borderColor: THEME_COLOR,
                          color: THEME_COLOR,
                          fontWeight: 600,
                          textTransform: 'none',
                          fontFamily: 'Manrope',
                          '&:hover': {
                            background: THEME_COLOR,
                            color: '#fff',
                            borderColor: THEME_COLOR,
                          },
                        }
                  }
                  onClick={() => {
                    if (step.redirectUrl) {
                      history.push(step.redirectUrl);
                    }
                  }}>
                  {step.buttonText}
                </Button>
              </Box>
            </ContentBox>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ProgressTracker;
